import is from '@sindresorhus/is';
import Maybe from 'true-myth/maybe';
import { z } from 'zod';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';
import type { PersistStorage } from 'zustand/middleware';
import { persist } from 'zustand/middleware';
import { createComputed } from 'zustand-computed';

import type { HardSoftSkillWithIconColor, LanguageSkillWithIconColor, Skill } from '~/shared/data-contract/skill';
import { hardSoftSkillProficiencyLevelSchema, languageSkillProficiencyLevelSchema } from '~/shared/data-contract/skill';
import type { LanguageCode, LanguageConfigMap, LanguageKey } from '~/shared/internationalization';
import type { Translate } from '~/shared/internationalization/translation';

import { isLanguageSkill } from '../../app/common/skills';
import type { CompanyOnboardingEmitter } from './company-onboarding-emitter';
import {
    fetchedCvAnalysisEvent,
    generateCompanyDescriptionEvent,
    generateCompanyPrioritiesEvent,
    generatedCompanyDescriptionEvent,
    generatedCompanyPrioritiesEvent,
    generateJobArchitectureEvent,
    refreshCompanyProfileEvent,
    refreshedCompanyProfileEvent,
    updateCompanyDescriptionEvent,
    updateCompanyProfileEvent,
    updatedCompanyDescriptionEvent,
    updatedCompanyProfileEvent,
    updatedUserSettingsEvent,
    updateUserSettingsEvent,
    uploadCvEvent,
} from './company-onboarding-emitter';
import type { OnboardingLabelsStoreState } from './company-onboarding-store-labels';
import { getTranslatedOnboardingLabels } from './company-onboarding-store-labels';
import type { CompanyProfile, CompanyProfileItem } from './company-profile';

const companyProfileStepSchema = z.union([z.literal('company-description'), z.literal('company-priorities')]);
const employeeProfileStepSchema = z.union([z.literal('cv-upload'), z.literal('review-skills')]);
const companyOnboardingStepSchema = z.union([
    ...companyProfileStepSchema.options,
    ...employeeProfileStepSchema.options,
]);

const companyOnboardingStepToStepRootSchema = companyOnboardingStepSchema.transform((value) => {
    if (value === 'company-description' || value === 'company-priorities') {
        return 'company-profile';
    }

    if (value === 'cv-upload' || value === 'review-skills') {
        return 'employee-profile';
    }

    return 'company-profile';
});

export type CompanyOnboardingStep = z.infer<typeof companyOnboardingStepSchema>;

export type CompanyOnboardingStepRoot = z.infer<typeof companyOnboardingStepToStepRootSchema>;

export type OnboardingType = 'company' | 'employee';

export type CompanyOnboardingState = {
    readonly availableLanguages: LanguageConfigMap;
    readonly companyOnboardingStep: CompanyOnboardingStep;
    readonly companyProfile: Maybe<CompanyProfile>;
    readonly employeeHardSoftSkills: Maybe<readonly HardSoftSkillWithIconColor[]>;
    readonly employeeLanguageSkills: Maybe<readonly LanguageSkillWithIconColor[]>;
    readonly isCvAnalysisLoading: boolean;
    readonly isGeneratingCompanyDescription: boolean;
    readonly isGeneratingCompanyPriorities: boolean;
    readonly onboardingType: OnboardingType;
    readonly selectedLanguage: LanguageCode;
    readonly uploadedCV: Maybe<File>;
};

export type CompanyOnboardingStoreState = CompanyOnboardingState & OnboardingLabelsStoreState;

export type CompanyOnboardingStoreActions = {
    readonly changeEmployeeSkillProficiency: (skillId: string, proficiency: number) => void;
    readonly finishOnboarding: () => void;
    readonly loadCompanyProfile: () => void;
    readonly nextStep: () => void;
    readonly previousStep: () => void;
    readonly setCompanyDescription: (description: Maybe<string>) => void;
    readonly setCompanyPriorities: (priorities: Maybe<string>) => void;
    readonly setSelectedLanguage: (language: LanguageCode, languageKey: LanguageKey) => void;
    readonly startCVAnalysis: () => void;
    readonly updateCompanyDescription: () => void;
    readonly updateCompanyProfile: () => void;
    readonly uploadCV: (file: Maybe<File>) => void;
};

export type CompanyOnboardingStore = CompanyOnboardingStoreActions & CompanyOnboardingStoreState;

export type ComputedStore = {
    readonly companyDescription: Maybe<string>;
    readonly companyOnboardingStepRoot: CompanyOnboardingStepRoot;
    readonly companyPriorities: Maybe<string>;
    readonly employeeHardSoftSkills: Maybe<readonly HardSoftSkillWithIconColor[]>;
    readonly employeeLanguageSkills: Maybe<readonly LanguageSkillWithIconColor[]>;
    readonly skillMosaicDimensions: readonly [number, number];
};

export type CompanyOnboardingWithComputedState = CompanyOnboardingStore & ComputedStore;

export type CompanyOnboardingStoreDependencies = {
    readonly availableLanguages: LanguageConfigMap;
    readonly companyOnboardingEmitter: CompanyOnboardingEmitter;
    readonly initialApplicationLanguage: LanguageCode;
    readonly navigateToHome: () => void;
    readonly onboardingType: OnboardingType;
    readonly persistentStorage?: PersistStorage<CompanyOnboardingStore>;
    readonly setApplicationLanguage: (language: LanguageKey) => void;
    readonly translate: Translate;
};

const isEmployeeOnboarding = (onboardingType: OnboardingType): onboardingType is 'employee' =>
    onboardingType === 'employee';

export type CompanyOnboardingStoreWithComputedStore = CompanyOnboardingStore & ComputedStore;

const companyOnboardingSteps: readonly CompanyOnboardingStep[] = [
    'company-description',
    'company-priorities',
    'cv-upload',
    'review-skills',
];

type CompanyProfileField = keyof Pick<CompanyProfile, 'description' | 'priorities'>;

const getCompanyFieldForLanguage = <K extends CompanyProfileField>(
    companyProfile: Maybe<CompanyProfile>,
    language: LanguageCode,
    field: K,
): Maybe<string> =>
    companyProfile
        .get(field)
        .andThen((items) => Maybe.of(items.find((item) => item.language === language)))
        .andThen((item) => Maybe.of(item.text));

const updateCompanyProfileItem = (
    items: Maybe<readonly CompanyProfileItem[]>,
    language: LanguageCode,
    newText: Maybe<string>,
): readonly CompanyProfileItem[] => {
    const textValue = newText.unwrapOr(null);

    if (items.isJust) {
        const existingItems = items.value;
        const existingItemIndex = existingItems.findIndex((item) => item.language === language);

        if (existingItemIndex !== -1) {
            return existingItems.map((item, index) =>
                index === existingItemIndex ? { language, text: textValue } : item,
            );
        }

        return [...existingItems, { language, text: textValue }];
    }

    return [{ language, text: textValue }];
};

const determineSkillMosaicGridDimensions = (skills: readonly Skill[]): readonly [number, number] => {
    const length = skills.length;
    const gridSize = Math.ceil(Math.sqrt(length));

    return [gridSize, gridSize];
};

const defaultSkillColor = '#FC521F';

const determineSkillIconColor = (skill: Skill): string => {
    const level = is.nullOrUndefined(skill.level) ? 0 : skill.level;

    if ((isLanguageSkill(skill) === true && level === 6) || (isLanguageSkill(skill) === false && level === 5)) {
        return defaultSkillColor;
    }

    const percentage = Math.round(255 * (1 - (level - 1) * 0.2));
    const hexValue = `#${percentage.toString(16).padStart(2, '0')}${percentage.toString(16).padStart(2, '0')}${percentage.toString(16).padStart(2, '0')}`;

    return hexValue;
};

export const createComputedStore = (): ((state: CompanyOnboardingStore) => ComputedStore) => {
    return (state) => {
        const {
            companyOnboardingStep,
            companyProfile,
            employeeHardSoftSkills,
            employeeLanguageSkills,
            selectedLanguage,
        } = state;
        const onboardingStepRoot = Maybe.of<CompanyOnboardingStepRoot>(
            companyOnboardingStepToStepRootSchema.safeParse(companyOnboardingStep).data,
        );

        const companyDescription = getCompanyFieldForLanguage(companyProfile, selectedLanguage, 'description');
        const companyPriorities = getCompanyFieldForLanguage(companyProfile, selectedLanguage, 'priorities');

        const skills = [...employeeHardSoftSkills.unwrapOr([]), ...employeeLanguageSkills.unwrapOr([])];

        return {
            companyDescription,
            companyOnboardingStepRoot: onboardingStepRoot.unwrapOr('company-profile'),
            companyPriorities,
            employeeHardSoftSkills: employeeHardSoftSkills.map((skills) => {
                return skills.map((skill) => ({
                    ...skill,
                    iconColor: determineSkillIconColor(skill),
                }));
            }),
            employeeLanguageSkills: employeeLanguageSkills.map((skills) => {
                return skills.map((skill) => ({
                    ...skill,
                    iconColor: determineSkillIconColor(skill),
                }));
            }),
            skillMosaicDimensions: is.emptyArray(skills) ? [0, 0] : determineSkillMosaicGridDimensions(skills),
        };
    };
};

export const createCompanyOnboardingStore = (
    dependencies: CompanyOnboardingStoreDependencies,
): StoreApi<CompanyOnboardingStoreWithComputedStore> => {
    const {
        availableLanguages,
        companyOnboardingEmitter,
        initialApplicationLanguage,
        navigateToHome,
        onboardingType,
        persistentStorage,
        setApplicationLanguage,
        translate,
    } = dependencies;

    const translateLabels = (): OnboardingLabelsStoreState => getTranslatedOnboardingLabels(translate);

    const computed = createComputed(createComputedStore());

    const computedStore = computed((setState, getState) => {
        const setCompanyProfileField = <K extends CompanyProfileField>(
            fieldName: K,
            newText: Maybe<string>,
        ): Maybe<CompanyProfile> => {
            const { companyProfile, selectedLanguage } = getState();

            return companyProfile.map((currentProfile) => {
                const currentItems = Maybe.of<CompanyProfileItem[]>(currentProfile[fieldName]);

                const updatedItems = updateCompanyProfileItem(currentItems, selectedLanguage, newText);

                return {
                    ...currentProfile,
                    [fieldName]: updatedItems,
                };
            });
        };

        const nextStep = (): void => {
            const { companyDescription, companyOnboardingStep, companyPriorities } = getState();
            // biome-ignore lint/complexity/useIndexOf: suppressed due to migration
            const stepIndex = companyOnboardingSteps.findIndex((step) => step === companyOnboardingStep);
            const nextStep = Maybe.of(companyOnboardingSteps[stepIndex + 1]).unwrapOr(companyOnboardingStep);

            if (
                companyOnboardingStep === 'company-priorities' &&
                companyDescription.isJust &&
                companyPriorities.isJust
            ) {
                companyOnboardingEmitter.emit(generateJobArchitectureEvent, {
                    companyDescription: companyDescription.value,
                    companyPriorities: companyPriorities.value,
                });
            }

            setState({
                companyOnboardingStep: nextStep,
            });
        };

        companyOnboardingEmitter.on(refreshedCompanyProfileEvent, ({ data: companyProfile }) => {
            setState({ companyProfile: Maybe.just(companyProfile) });
            const companyDescription = getCompanyFieldForLanguage(
                Maybe.of(companyProfile),
                initialApplicationLanguage,
                'description',
            );

            if (companyDescription.isJust) {
                return;
            }

            setState({
                isGeneratingCompanyDescription: true,
            });

            companyOnboardingEmitter.emit(generateCompanyDescriptionEvent);
        });

        companyOnboardingEmitter.on(generatedCompanyDescriptionEvent, ({ data: companyProfile }) => {
            setState({ companyProfile: Maybe.just(companyProfile), isGeneratingCompanyDescription: false });
        });

        companyOnboardingEmitter.on(generatedCompanyPrioritiesEvent, ({ data: companyProfile }) => {
            setState({ companyProfile: Maybe.just(companyProfile), isGeneratingCompanyPriorities: false });
        });

        companyOnboardingEmitter.on(fetchedCvAnalysisEvent, ({ data: cvAnalysis }) => {
            if (cvAnalysis.status === 'success') {
                setState({ isCvAnalysisLoading: false });
                setState({
                    employeeHardSoftSkills: Maybe.just(
                        cvAnalysis.profile.hardSoftSkills.map((skill) => ({
                            ...skill,
                            iconColor: defaultSkillColor,
                        })),
                    ),
                    employeeLanguageSkills: Maybe.just(
                        cvAnalysis.profile.languages.map((skill) => ({
                            ...skill,
                            iconColor: defaultSkillColor,
                        })),
                    ),
                });
                nextStep();
            }
        });

        companyOnboardingEmitter.on(updatedCompanyProfileEvent, ({ data: companyProfile }) => {
            setState({ companyProfile: Maybe.just(companyProfile) });
        });

        companyOnboardingEmitter.on(updatedCompanyDescriptionEvent, ({ data: companyProfile }) => {
            const { selectedLanguage } = getState();

            if (getCompanyFieldForLanguage(Maybe.of(companyProfile), selectedLanguage, 'priorities').isNothing) {
                companyOnboardingEmitter.emit(generateCompanyPrioritiesEvent);
                setState({ companyProfile: Maybe.just(companyProfile), isGeneratingCompanyPriorities: true });
            } else {
                setState({ companyProfile: Maybe.just(companyProfile) });
            }
        });

        companyOnboardingEmitter.on(updatedUserSettingsEvent, () => {
            navigateToHome();
        });

        return {
            ...translateLabels(),
            availableLanguages,
            changeEmployeeSkillProficiency: (skillId, skillLevel) => {
                const { employeeHardSoftSkills, employeeLanguageSkills } = getState();

                const hardSoftSkillToChange = employeeHardSoftSkills.andThen((skills) => {
                    return Maybe.of(skills.find((skill) => skill.id === skillId));
                });

                const hardSoftSkillLevel = hardSoftSkillProficiencyLevelSchema.safeParse(skillLevel);

                if (hardSoftSkillToChange.isJust && hardSoftSkillLevel.success) {
                    setState({
                        employeeHardSoftSkills: employeeHardSoftSkills.map((skills) => {
                            return skills.map((skill) => {
                                return skill.id === skillId
                                    ? {
                                          ...skill,
                                          level: hardSoftSkillLevel.data,
                                      }
                                    : skill;
                            });
                        }),
                    });
                }

                const languageSkillToChange = employeeLanguageSkills.andThen((skills) => {
                    return Maybe.of(skills.find((skill) => skill.id === skillId));
                });

                const languageSkillLevel = languageSkillProficiencyLevelSchema.safeParse(skillLevel);

                if (languageSkillToChange.isJust && languageSkillLevel.success) {
                    setState({
                        employeeLanguageSkills: employeeLanguageSkills.map((skills) => {
                            return skills.map((skill) => {
                                return skill.id === skillId
                                    ? {
                                          ...skill,
                                          level: languageSkillLevel.data,
                                      }
                                    : skill;
                            });
                        }),
                    });
                }
            },
            companyOnboardingStep: isEmployeeOnboarding(onboardingType) ? 'cv-upload' : 'company-description',
            companyProfile: Maybe.nothing(),
            employeeHardSoftSkills: Maybe.nothing(),
            employeeLanguageSkills: Maybe.nothing(),
            finishOnboarding: () => {
                companyOnboardingEmitter.emit(updateUserSettingsEvent, {
                    startPage: 'home',
                });
            },
            isCvAnalysisLoading: false,
            isGeneratingCompanyDescription: false,
            isGeneratingCompanyPriorities: false,
            loadCompanyProfile: () => {
                companyOnboardingEmitter.emit(refreshCompanyProfileEvent);
            },
            nextStep,
            onboardingType,
            previousStep: () => {
                const { companyOnboardingStep } = getState();
                // biome-ignore lint/complexity/useIndexOf: suppressed due to migration
                const stepIndex = companyOnboardingSteps.findIndex((step) => step === companyOnboardingStep);
                const previousStep = Maybe.of(companyOnboardingSteps[stepIndex - 1]).unwrapOr(companyOnboardingStep);

                setState({
                    companyOnboardingStep: previousStep,
                });
            },
            selectedLanguage: initialApplicationLanguage,
            setCompanyDescription: (newDescription) => {
                const updatedCompanyProfile = setCompanyProfileField('description', newDescription);

                setState({
                    companyProfile: updatedCompanyProfile,
                });
            },
            setCompanyPriorities: (newPriorities) => {
                const updatedCompanyProfile = setCompanyProfileField('priorities', newPriorities);

                setState({
                    companyProfile: updatedCompanyProfile,
                });
            },
            setSelectedLanguage: (selectedLanguageCode, selectedLanguageKey) => {
                setApplicationLanguage(selectedLanguageKey);
                setState({
                    selectedLanguage: selectedLanguageCode,
                    ...translateLabels(),
                });
            },
            startCVAnalysis: () => {
                const { uploadedCV } = getState();

                if (uploadedCV.isNothing) {
                    return;
                }

                setState({ isCvAnalysisLoading: true });
                companyOnboardingEmitter.emit(uploadCvEvent, uploadedCV.value);
            },
            updateCompanyDescription: () => {
                const { companyProfile, nextStep } = getState();

                if (companyProfile.isNothing) {
                    return;
                }

                companyOnboardingEmitter.emit(updateCompanyDescriptionEvent, companyProfile.value);

                nextStep();
            },
            updateCompanyProfile: () => {
                const { companyProfile, nextStep } = getState();

                if (companyProfile.isNothing) {
                    return;
                }

                companyOnboardingEmitter.emit(updateCompanyProfileEvent, companyProfile.value);

                nextStep();
            },
            uploadCV: (file) => {
                setState({
                    uploadedCV: file,
                });
            },
            uploadedCV: Maybe.nothing(),
        };
    });

    const persistentStore = persist(computedStore, {
        name: 'company-onboarding-store',
        storage: persistentStorage,
    });

    return createStore<CompanyOnboardingStore>()(persistentStore);
};
